import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  Card,
  CardContent,
  Chip,
  Button,
  Tooltip
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Download as DownloadIcon,
  Videocam as VideocamIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { getRecordingsForEvent } from '../../services/virtualEventService';

interface Recording {
  id: string;
  eventId: string;
  roomName: string;
  recordingUrl: string;
  startTime?: Date;
  endTime?: Date;
  duration?: number; // in seconds
  size?: number; // in bytes
  createdAt: Date;
}

interface RecordingsListProps {
  eventId: string;
}

const RecordingsList: React.FC<RecordingsListProps> = ({ eventId }) => {
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchRecordings = async () => {
      try {
        setLoading(true);
        const sessions = await getRecordingsForEvent(eventId);
        
        // Map sessions to recordings format
        const recordingsData = sessions
          .filter(session => session.recordingUrl) // Only include sessions with recordings
          .map(session => ({
            id: session.id || '',
            eventId: session.eventId,
            roomName: session.roomName,
            recordingUrl: session.recordingUrl || '',
            startTime: session.startTime,
            endTime: session.endTime,
            duration: session.startTime && session.endTime 
              ? Math.floor((session.endTime.getTime() - session.startTime.getTime()) / 1000) 
              : undefined,
            createdAt: session.createdAt
          }));
        
        setRecordings(recordingsData);
      } catch (err) {
        console.error('Error fetching recordings:', err);
        setError('Failed to load recordings');
      } finally {
        setLoading(false);
      }
    };
    
    fetchRecordings();
  }, [eventId]);
  
  const formatDuration = (seconds?: number): string => {
    if (!seconds) return 'Unknown length';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else {
      return `${minutes}m ${secs}s`;
    }
  };
  
  const formatDateTime = (date?: Date): string => {
    if (!date) return 'Unknown date';
    return date.toLocaleString();
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }
  
  if (recordings.length === 0) {
    return (
      <Alert severity="info" icon={<VideocamIcon />} sx={{ mb: 3 }}>
        No recordings are available for this event yet.
      </Alert>
    );
  }
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Event Recordings
      </Typography>
      
      <List sx={{ mb: 3 }}>
        {recordings.map((recording) => (
          <Card key={recording.id} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="subtitle1" component="div">
                  <VideocamIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                  Recording: {recording.roomName}
                </Typography>
                <Chip 
                  label={formatDuration(recording.duration)} 
                  size="small"
                  icon={<TimeIcon />}
                  variant="outlined"
                />
              </Box>
              
              <Box sx={{ color: 'text.secondary', mb: 2 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                  <CalendarIcon sx={{ mr: 1, fontSize: '0.9rem' }} />
                  Recorded: {formatDateTime(recording.startTime)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  component="a"
                  href={recording.recordingUrl}
                  download
                >
                  Download
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PlayIcon />}
                  component="a"
                  href={recording.recordingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Play Recording
                </Button>
              </Box>
            </CardContent>
          </Card>
        ))}
      </List>
    </Box>
  );
};

export default RecordingsList; 